import PropTypes from "prop-types";
import Icon from "@react-native-vector-icons/material-design-icons";
import theme from "assets/theme";
import moment from "moment";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { ALERT_TYPE_CRITICAL, ALERT_TYPE_INFO } from "shared/constants";
import AlertService from "services/Alert.service";
import CustomModal from "webComponentsFormElements/CustomModal.component";
import CustomButton from "webComponentsFormElements/inputs/CustomButton.component";
import SearchBar from "webComponentsFormElements/inputs/SearchBar.component";
import AlertCard from "webComponentsScreens/alerts/AlertCard.component";
import CreateAlertModal from "webComponentsScreens/alerts/CreateAlertModal.component";
import Banner from "webComponentsShared/Banner.component";
import { AlertContext } from "webContext/Alert.context";
import { AuthContext } from "webContext/Auth.context";
import { OrganizationContext } from "webContext/Organization.context";
import { ToastContext } from "webContext/Toast.context";

const HISTORY_SORT_FIELDS = [
  { key: "startTime", label: "Start" },
  { key: "dismissedAt", label: "Resolved" },
  { key: "lastEdited", label: "Last Edited" },
  { key: "name", label: "Name" },
  { key: "reporter", label: "Reporter" },
];

const classes = StyleSheet.create({
  root: {
    backgroundColor: theme.color.background,
    flex: 1,
    overflow: "auto",
  },
  alertsContainer: {
    marginTop: 10,
    flexDirection: "row",
    flexWrap: "wrap",
    overflow: "auto",
  },
  banner: {
    flexDirection: "row",
    gap: 50,
    alignItems: "center",
  },
  tabRow: {
    flexDirection: "row",
    paddingHorizontal: 10,
    marginTop: 10,
    borderBottomWidth: 1,
    borderBottomColor: theme.color.lightGrey,
  },
  tab: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    marginBottom: -1,
  },
  tabText: {
    fontSize: 15,
    fontWeight: theme.font.weight.medium,
    color: theme.color.darkGrey,
  },
  staleBanner: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: theme.color.lightGrey,
    borderRadius: 8,
    marginHorizontal: 10,
    marginTop: 12,
    paddingVertical: 10,
    paddingHorizontal: 16,
    gap: 12,
  },
  staleText: {
    flex: 1,
    fontSize: 14,
    color: theme.color.darkGrey,
    fontWeight: theme.font.weight.medium,
  },
  // History section
  historyContainer: {
    marginTop: 24,
    marginHorizontal: 10,
    marginBottom: 24,
  },
  historySectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: theme.color.lightGrey,
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  historyHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  historyTitle: {
    fontSize: theme.font.size.subHeader,
    fontWeight: theme.font.weight.semibold,
    color: theme.color.darkGrey,
  },
  historyCountBadge: {
    backgroundColor: theme.color.greyHighlight,
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  historyCountText: {
    fontSize: theme.font.size.small,
    fontWeight: theme.font.weight.semibold,
    color: theme.color.darkGrey,
  },
  historyControls: {
    paddingTop: 10,
    paddingBottom: 4,
    gap: 8,
  },
  sortRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 6,
  },
  sortLabel: {
    fontSize: theme.font.size.small,
    color: theme.color.darkGrey,
    fontWeight: theme.font.weight.medium,
    marginRight: 2,
  },
  sortChip: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 16,
    backgroundColor: theme.color.grey,
    gap: 3,
  },
  sortChipActive: {
    backgroundColor: theme.color.primary,
  },
  sortChipText: {
    fontSize: theme.font.size.small,
    color: theme.color.darkGrey,
  },
  sortChipTextActive: {
    color: theme.color.background,
    fontWeight: theme.font.weight.medium,
  },
  historyCard: {
    flexDirection: "row",
    marginTop: 8,
    borderRadius: 10,
    backgroundColor: theme.color.background,
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    overflow: "hidden",
    borderWidth: 1,
    borderColor: theme.color.grey,
  },
  historyCardPressed: {
    opacity: 0.7,
  },
  historyCardAccent: {
    width: 5,
  },
  historyCardContent: {
    flex: 1,
    padding: 12,
    gap: 6,
  },
  historyCardHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 8,
  },
  historyCardTitle: {
    flex: 1,
    fontSize: theme.font.size.body,
    fontWeight: theme.font.weight.semibold,
    color: theme.color.black,
  },
  statusBadge: {
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
    alignSelf: "flex-start",
  },
  statusBadgeText: {
    fontSize: 11,
    fontWeight: theme.font.weight.bold,
    color: theme.color.background,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  historyMetaRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  historyTimesRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  historyMetaItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  historyMetaValue: {
    fontSize: theme.font.size.small,
    color: theme.color.darkGrey,
  },
  resolutionPreview: {
    fontSize: theme.font.size.small,
    color: theme.color.darkGrey,
    fontStyle: "italic",
    borderLeftWidth: 2,
    borderLeftColor: theme.color.greyHighlight,
    paddingLeft: 8,
    marginTop: 2,
  },
  emptyHistory: {
    textAlign: "center",
    color: theme.color.darkGrey,
    fontStyle: "italic",
    paddingVertical: 20,
    fontSize: theme.font.size.body,
  },
});

export default function AlertsScreen({ route }) {
  const { getAlerts, sortedAllAlerts } = useContext(AlertContext);
  const { isAdmin, isMessagingManager } = useContext(AuthContext);
  const { showToast } = useContext(ToastContext);
  const {
    getOrganizationSystems,
    getTeams,
    getLocations,
    getPersons,
    organizationSystems,
    teamList,
    locationList,
    personList,
    organizationColor,
  } = useContext(OrganizationContext);

  const filterOption = "all";
  const [isLoading, setIsLoading] = useState(false);
  const [isBatchResolving, setIsBatchResolving] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [activeTab, setActiveTab] = useState(route?.params?.alertType || ALERT_TYPE_CRITICAL);
  const [creatingAlertType, setCreatingAlertType] = useState(null);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(true);
  const [historySearch, setHistorySearch] = useState("");
  const [historySortField, setHistorySortField] = useState("startTime");
  const [historySortDir, setHistorySortDir] = useState("desc");

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);

      const fetchStatus = {
        getAlerts: { called: false, reason: "" },
        getOrganizationSystems: { called: false, reason: "" },
        getTeams: { called: false, reason: "" },
        getLocations: { called: false, reason: "" },
        getPersons: { called: false, reason: "" },
      };

      const promises = [];

      if (sortedAllAlerts.size === 0) {
        promises.push(getAlerts());
        fetchStatus.getAlerts = { called: true, reason: "No alerts in state" };
      } else {
        fetchStatus.getAlerts = {
          called: false,
          reason: `${sortedAllAlerts.size} alerts already loaded`,
        };
      }

      if (organizationSystems.length === 0) {
        promises.push(getOrganizationSystems());
        fetchStatus.getOrganizationSystems = {
          called: true,
          reason: "No systems in state",
        };
      } else {
        fetchStatus.getOrganizationSystems = {
          called: false,
          reason: `${organizationSystems.length} systems already loaded`,
        };
      }

      if (teamList.length === 0) {
        promises.push(getTeams());
        fetchStatus.getTeams = { called: true, reason: "No teams in state" };
      } else {
        fetchStatus.getTeams = {
          called: false,
          reason: `${teamList.length} teams already loaded`,
        };
      }

      if (locationList.length === 0) {
        promises.push(getLocations());
        fetchStatus.getLocations = {
          called: true,
          reason: "No locations in state",
        };
      } else {
        fetchStatus.getLocations = {
          called: false,
          reason: `${locationList.length} locations already loaded`,
        };
      }

      if (personList.length === 0) {
        promises.push(getPersons());
        fetchStatus.getPersons = {
          called: true,
          reason: "No persons in state",
        };
      } else {
        fetchStatus.getPersons = {
          called: false,
          reason: `${personList.length} persons already loaded`,
        };
      }

      await Promise.all(promises);
      setIsLoading(false);
    };
    fetchData();
  }, [
    getAlerts,
    getOrganizationSystems,
    getTeams,
    getLocations,
    getPersons,
    sortedAllAlerts.size,
    organizationSystems.length,
    teamList.length,
    locationList.length,
    personList.length,
  ]);

  const filteredAlerts = useMemo(() => {
    const all = [...sortedAllAlerts.values()];
    return all
      .filter(
        (alert) =>
          (alert.type || ALERT_TYPE_CRITICAL) === activeTab &&
          (alert.title.toLowerCase().includes(searchText.toLowerCase()) ||
            alert.description
              ?.toLowerCase()
              .includes(searchText.toLowerCase())) &&
          (filterOption === "all" ||
            (filterOption === "active" ? alert.isActive : !alert.isActive)),
      )
      .sort((a, b) => new Date(b.startDatetime) - new Date(a.startDatetime));
  }, [searchText, sortedAllAlerts, filterOption, activeTab]);

  const activeAlerts = filteredAlerts.filter((a) => a.isActive);
  const dismissedAlerts = filteredAlerts.filter(
    (a) => (showDeleted || !a.isDeleted) && !a.isActive,
  );

  const handleToggleShowDeleted = useCallback(async () => {
    const next = !showDeleted;
    setShowDeleted(next);
    await getAlerts(next);
  }, [showDeleted, getAlerts]);

  const staleInfoAlerts = useMemo(() => {
    if (activeTab !== ALERT_TYPE_INFO) {
      return [];
    }
    const now = new Date();
    return [...sortedAllAlerts.values()].filter(
      (a) =>
        (a.type || ALERT_TYPE_CRITICAL) === ALERT_TYPE_INFO &&
        a.isActive &&
        !a.isDeleted &&
        a.expirationDatetime &&
        new Date(a.expirationDatetime) < now,
    );
  }, [sortedAllAlerts, activeTab]);

  const handleBatchResolve = useCallback(async () => {
    setIsBatchResolving(true);
    try {
      await AlertService.dismissAlerts({
        alertIds: staleInfoAlerts.map((a) => a.alertId),
        resolution: "Batch resolved — stale info message.",
      });
      await getAlerts(showDeleted);
      showToast(
        `${staleInfoAlerts.length} stale info message${staleInfoAlerts.length > 1 ? "s" : ""} resolved.`,
        "success",
      );
    } catch {
      showToast("Failed to batch resolve info messages.", "error");
    } finally {
      setIsBatchResolving(false);
    }
  }, [staleInfoAlerts, getAlerts, showToast, showDeleted]);

  const toggleHistorySort = useCallback(
    (field) => {
      setHistorySortDir((prev) => {
        if (historySortField === field) {
          return prev === "asc" ? "desc" : "asc";
        }
        return "desc";
      });
      setHistorySortField(field);
    },
    [historySortField],
  );

  const sortedDismissedAlerts = useMemo(() => {
    const search = historySearch.toLowerCase();
    const filtered = dismissedAlerts.filter((a) => {
      const reporter = a._associations?.createUser
        ? `${a._associations.createUser.firstName} ${a._associations.createUser.lastName}`
        : "";
      return (
        a.title.toLowerCase().includes(search) ||
        reporter.toLowerCase().includes(search)
      );
    });
    return filtered.sort((a, b) => {
      let aVal, bVal;
      switch (historySortField) {
        case "name":
          aVal = a.title;
          bVal = b.title;
          break;
        case "reporter":
          aVal = a._associations?.createUser
            ? `${a._associations.createUser.firstName} ${a._associations.createUser.lastName}`
            : "";
          bVal = b._associations?.createUser
            ? `${b._associations.createUser.firstName} ${b._associations.createUser.lastName}`
            : "";
          break;
        case "dismissedAt":
          aVal = new Date(a.dismissedAt || 0);
          bVal = new Date(b.dismissedAt || 0);
          break;
        case "lastEdited":
          aVal = new Date(a.changeDatetime || 0);
          bVal = new Date(b.changeDatetime || 0);
          break;
        default:
          aVal = new Date(a.startDatetime || 0);
          bVal = new Date(b.startDatetime || 0);
      }
      if (typeof aVal === "string") {
        return historySortDir === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return historySortDir === "asc" ? aVal - bVal : bVal - aVal;
    });
  }, [dismissedAlerts, historySearch, historySortField, historySortDir]);

  if (isLoading) {
    return <ActivityIndicator size="large" />;
  }

  return (
    <View style={classes.root}>
      <CreateAlertModal
        visible={creatingAlertType !== null}
        alertType={creatingAlertType}
        showDeleted={showDeleted}
        onClose={() => setCreatingAlertType(null)}
      />

      <CustomModal
        open={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        title={selectedAlert ? selectedAlert.title : "Details"}
        scroll
      >
        {selectedAlert && (
          <AlertCard
            alert={selectedAlert}
            fromTable
            showDeleted={showDeleted}
            onDelete={() => setIsModalVisible(false)}
          />
        )}
      </CustomModal>

      <Banner classes={classes.banner}>
        <SearchBar
          placeholder={
            activeTab === ALERT_TYPE_CRITICAL
              ? "Search alerts..."
              : "Search info messages..."
          }
          value={searchText}
          onChangeText={setSearchText}
        />
        <CustomButton
          title="Create Alert"
          icon="plus-circle-outline"
          variant="dark"
          onPress={() => setCreatingAlertType(ALERT_TYPE_CRITICAL)}
        />
        {(isAdmin || isMessagingManager) && (
          <CustomButton
            title="Create Info Message"
            icon="information-outline"
            variant="dark"
            onPress={() => setCreatingAlertType(ALERT_TYPE_INFO)}
          />
        )}
        {(isAdmin || isMessagingManager) && (
          <TouchableOpacity
            onPress={handleToggleShowDeleted}
            style={{ flexDirection: "row", alignItems: "center", gap: 6 }}
          >
            <Icon
              name={showDeleted ? "checkbox-marked" : "checkbox-blank-outline"}
              size={22}
              color={theme.color.darkGrey}
            />
            <Text style={classes.dropDownTextStyle}>Show deleted</Text>
          </TouchableOpacity>
        )}
      </Banner>

      <View style={classes.tabRow}>
        {[
          { type: ALERT_TYPE_CRITICAL, label: "Critical Alerts" },
          { type: ALERT_TYPE_INFO, label: "Info Messages" },
        ].map(({ type, label }) => {
          const isActive = activeTab === type;
          return (
            <TouchableOpacity
              key={type}
              style={[
                classes.tab,
                isActive && {
                  borderBottomWidth: 2,
                  borderBottomColor: organizationColor || theme.color.darkGrey,
                },
              ]}
              onPress={() => {
                setActiveTab(type);
                setSearchText("");
              }}
            >
              <Text
                style={[
                  classes.tabText,
                  isActive && {
                    color: organizationColor || theme.color.darkGrey,
                  },
                ]}
              >
                {label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {activeTab === ALERT_TYPE_INFO &&
        staleInfoAlerts.length > 0 &&
        (isAdmin || isMessagingManager) && (
          <View style={classes.staleBanner}>
            <Text style={classes.staleText}>
              {staleInfoAlerts.length} info message
              {staleInfoAlerts.length > 1 ? "s have" : " has"} passed their
              expiration date and may need attention.
            </Text>
            <CustomButton
              title="Batch Resolve"
              variant="danger"
              icon="check-all"
              onPress={handleBatchResolve}
              disabled={isBatchResolving}
              isLoading={isBatchResolving}
            />
          </View>
        )}

      <ScrollView>
        <View style={classes.alertsContainer}>
          {activeAlerts.map((alert) => (
            <AlertCard
              key={alert.alertId}
              alert={alert}
              openAlert={route?.params?.alertId === alert?.alertId}
              showDeleted={showDeleted}
            />
          ))}
        </View>

        {dismissedAlerts.length > 0 && (
          <View style={classes.historyContainer}>
            <Pressable
              style={classes.historySectionHeader}
              onPress={() => setHistoryExpanded((value) => !value)}
            >
              <View style={classes.historyHeaderLeft}>
                <Text style={classes.historyTitle}>
                  {activeTab === ALERT_TYPE_CRITICAL
                    ? "Alert History"
                    : "Info Message History"}
                </Text>
                <View style={classes.historyCountBadge}>
                  <Text style={classes.historyCountText}>
                    {dismissedAlerts.length}
                  </Text>
                </View>
              </View>
              <Icon
                name={historyExpanded ? "chevron-up" : "chevron-down"}
                size={22}
                color={theme.color.darkGrey}
              />
            </Pressable>

            {historyExpanded && (
              <View>
                <View style={classes.historyControls}>
                  <SearchBar
                    placeholder={
                      activeTab === ALERT_TYPE_CRITICAL
                        ? "Search alert history..."
                        : "Search info message history..."
                    }
                    value={historySearch}
                    onChangeText={setHistorySearch}
                  />
                  <View style={classes.sortRow}>
                    <Text style={classes.sortLabel}>Sort:</Text>
                    {HISTORY_SORT_FIELDS.map((field) => {
                      const isActive = historySortField === field.key;
                      return (
                        <Pressable
                          key={field.key}
                          style={[
                            classes.sortChip,
                            isActive && classes.sortChipActive,
                          ]}
                          onPress={() => toggleHistorySort(field.key)}
                        >
                          <Text
                            style={[
                              classes.sortChipText,
                              isActive && classes.sortChipTextActive,
                            ]}
                            testID={`sort-${field.label}`}
                          >
                            {field.label}
                          </Text>
                          {isActive && (
                            <Text style={classes.sortChipTextActive}>
                              {historySortDir === "asc" ? "↑" : "↓"}
                            </Text>
                          )}
                        </Pressable>
                      );
                    })}
                  </View>
                </View>

                {sortedDismissedAlerts.length === 0 ? (
                  <Text style={classes.emptyHistory}>No results found.</Text>
                ) : (
                  sortedDismissedAlerts.map((alert) => {
                    const statusColor = theme.color.darkGreen;
                    const reporter = alert._associations?.createUser
                      ? `${alert._associations.createUser.firstName} ${alert._associations.createUser.lastName}`
                      : "Unknown";

                    return (
                      <Pressable
                        key={alert.alertId}
                        style={({ pressed }) => [
                          classes.historyCard,
                          pressed && classes.historyCardPressed,
                        ]}
                        onPress={() => {
                          setSelectedAlert(alert);
                          setIsModalVisible(true);
                        }}
                      >
                        <View
                          style={[
                            classes.historyCardAccent,
                            { backgroundColor: statusColor },
                          ]}
                        />
                        <View style={classes.historyCardContent}>
                          <View style={classes.historyCardHeader}>
                            <Text
                              style={classes.historyCardTitle}
                              numberOfLines={2}
                              testID="alert-title"
                            >
                              {alert.title}
                            </Text>
                            <View
                              style={[
                                classes.statusBadge,
                                { backgroundColor: statusColor },
                              ]}
                            >
                              <Text style={classes.statusBadgeText}>
                                Resolved
                              </Text>
                            </View>
                          </View>

                          <View style={classes.historyMetaRow}>
                            <View style={classes.historyMetaItem}>
                              <Icon
                                name="account"
                                size={13}
                                color={theme.color.darkGrey}
                              />
                              <Text style={classes.historyMetaValue}>
                                {reporter}
                              </Text>
                            </View>
                            <View style={classes.historyMetaItem}>
                              <Icon
                                name="clock-edit-outline"
                                size={13}
                                color={theme.color.darkGrey}
                              />
                              <Text style={classes.historyMetaValue}>
                                {alert.changeDatetime
                                  ? moment(alert.changeDatetime).format(
                                      "MMM D, YYYY h:mm A",
                                    )
                                  : "--"}
                              </Text>
                            </View>
                          </View>

                          <View style={classes.historyTimesRow}>
                            <View style={classes.historyMetaItem}>
                              <Icon
                                name="calendar-start"
                                size={13}
                                color={theme.color.darkGrey}
                              />
                              <Text style={classes.historyMetaValue}>
                                {moment(alert.startDatetime).format(
                                  "MMM D, YYYY h:mm A",
                                )}
                              </Text>
                            </View>
                            <View style={classes.historyMetaItem}>
                              <Icon
                                name="calendar-check"
                                size={13}
                                color={statusColor}
                              />
                              <Text
                                style={[
                                  classes.historyMetaValue,
                                  { color: statusColor },
                                ]}
                              >
                                {alert.dismissedAt
                                  ? moment(alert.dismissedAt).format(
                                      "MMM D, YYYY h:mm A",
                                    )
                                  : "--"}
                              </Text>
                            </View>
                          </View>

                          {alert.resolution &&
                            alert.resolution !== "No resolution provided" && (
                              <Text
                                style={classes.resolutionPreview}
                                numberOfLines={2}
                              >
                                {alert.resolution}
                              </Text>
                            )}
                        </View>
                      </Pressable>
                    );
                  })
                )}
              </View>
            )}
          </View>
        )}
      </ScrollView>
    </View>
  );
}

AlertsScreen.propTypes = {
  route: PropTypes.shape({
    params: PropTypes.shape({
      alertType: PropTypes.string.isRequired,
    }).isRequired
  }).isRequired,
};